/**
 * @ipc/admin-ui · shared/lib/theme — управление темой (light/dark) для SPA.
 *
 * No-flash: класс .dark ставится инлайн-скриптом в index.html до рендера React
 * (читает cookie/localStorage ipc-theme, дефолт dark). Здесь — реактивный доступ
 * к текущей теме и её переключение (пишет .dark на <html> + cookie + localStorage).
 * Конвенция shadcn: тёмная тема через класс .dark на корневом элементе.
 */
import * as React from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ipc-theme';

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') {
    return 'dark';
  }
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage недоступен (private mode) — cookie ниже всё равно сохранит выбор.
  }
  document.cookie = `${STORAGE_KEY}=${theme}; path=/; max-age=31536000; samesite=strict`;
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [theme, setThemeState] = React.useState<Theme>(readInitialTheme);

  const setTheme = React.useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  const value = React.useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Текущая тема + сеттер. Вне ThemeProvider — безопасный дефолт 'dark' без записи. */
export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (ctx === null) {
    return { theme: 'dark', setTheme: applyTheme };
  }
  return ctx;
}
